import type { ReactNode } from 'react';
import { useLocale } from '@/i18n/locale-context';
import { LoadingRegion, Skeleton, SkeletonText, cn } from '@/components/ui';

/**
 * The pieces every public route is assembled from — phases.md Phase 7: "route skeleton
 * for every public page, each rendering a loading skeleton — the full site navigable
 * before any page has real content."
 *
 * The skeletons are shaped like the content that will replace them (a four-card grid
 * stays a four-card grid), which is the only version of this that is worth building: a
 * generic grey rectangle tells a reader nothing and shifts the layout the moment data
 * arrives. Phases 8–13 swap the skeleton for the real section and keep the frame.
 */

export function Section({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('px-gutter py-7', className)}>
      {(title || action) && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          {title && (
            <h2 className="flex items-center gap-2.5 text-2xl font-extrabold text-ink before:h-5 before:w-1 before:rounded-sm before:bg-red before:content-['']">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** The page's own heading block — a listing's title and its description. */
export function PageHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className="border-b border-line bg-soft px-gutter py-7">
      <h1 className="text-4xl font-extrabold text-ink">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-md text-body">{subtitle}</p>}
    </header>
  );
}

export function CardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-line bg-white">
      <Skeleton rounded="none" className="h-[150px] w-full" />
      <div className="flex flex-col gap-3 px-4 pb-4 pt-3.5">
        <Skeleton rounded="sm" className="h-2.5 w-16" />
        <SkeletonText lines={2} />
        <Skeleton rounded="sm" className="h-2.5 w-24" />
      </div>
    </div>
  );
}

export function CardGridSkeleton({ count = 4, label }: { count?: number; label: string }) {
  return (
    <LoadingRegion label={label} className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <CardSkeleton key={index} />
      ))}
    </LoadingRegion>
  );
}

export function ListSkeleton({ rows = 5, label }: { rows?: number; label: string }) {
  return (
    <LoadingRegion label={label} className="flex flex-col">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex gap-3 border-b border-line py-3.5 last:border-b-0">
          <Skeleton className="h-14 w-20 shrink-0" />
          <div className="flex-1">
            <SkeletonText lines={2} />
          </div>
        </div>
      ))}
    </LoadingRegion>
  );
}

export function HeroSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label} className="bg-soft px-gutter py-6">
      <div className="grid gap-0 overflow-hidden rounded-sm bg-white lg:grid-cols-hero">
        <Skeleton rounded="none" className="min-h-[320px] lg:min-h-[500px]" />
        <div className="hidden flex-col gap-4 px-5 py-6 lg:flex">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="border-b border-line pb-3.5 last:border-b-0">
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
      </div>
    </LoadingRegion>
  );
}

/** A 16:9 player frame, so the video page reserves its box before the embed loads. */
export function Skeleton16x9({ label }: { label: string }) {
  return (
    <LoadingRegion label={label} className="mx-auto max-w-4xl">
      <Skeleton rounded="lg" className="aspect-video w-full" />
      <SkeletonText lines={2} className="mt-4" />
    </LoadingRegion>
  );
}

export function ArticleSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label} className="mx-auto max-w-3xl px-gutter py-8">
      <Skeleton rounded="sm" className="mb-4 h-3 w-24" />
      <SkeletonText lines={2} className="mb-5" />
      <Skeleton className="mb-6 aspect-[3/2] w-full" />
      <SkeletonText lines={8} />
    </LoadingRegion>
  );
}

/**
 * Wraps a route body that has no data yet. Phases 8–13 delete the `<Placeholder>` and
 * render the real section — the surrounding `<Section>`/`<PageHeader>` frame stays.
 */
export function Placeholder({ phase, children }: { phase: string; children: ReactNode }) {
  const { t } = useLocale();
  return (
    <>
      {children}
      <p className="px-gutter pb-8 text-xs text-muted" data-testid="phase-placeholder">
        {t('status.comingIn', { phase })}
      </p>
    </>
  );
}
